import { useState, useRef, type FormEvent } from "react";
import { apiRequest } from "@/lib/queryClient";
import { toast } from "sonner";
import { getAdClickIds, getLeadAttribution } from "@/lib/lead-attribution";

/* ─── Shared constants (single source of truth) ───────────────────────────── */

const BLOCKED_DOMAINS = [
  "gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "aol.com",
  "icloud.com", "mail.com", "protonmail.com", "live.com",
];

export const INTEREST_OPTIONS = [
  "Performance Marketing",
  "SEO / AEO",
  "AI-First Creative",
  "AI Training & Integration",
  "Measurement & Reporting",
  "Something else",
];

/* ─── Theme tokens ────────────────────────────────────────────────────────── */

interface ThemeTokens {
  text: string;
  textMuted: string;
  inputBg: string;
  inputBgFocus: string;
  inputBorder: string;
  inputBorderFocus: string;
  checkBorder: string;
  checkBorderActive: string;
  checkBg: string;
  checkRowBg: string;
  gridBorder: string;
  accent: string;
  btnBg: string;
  btnBgHover: string;
  btnText: string;
}

const themes: Record<"dark" | "light", ThemeTokens> = {
  dark: {
    text: "var(--cream)",
    textMuted: "var(--cream-70)",
    inputBg: "var(--input-bg-dark)",
    inputBgFocus: "var(--input-bg-dark-focus)",
    inputBorder: "var(--input-border-dark)",
    inputBorderFocus: "var(--cream)",
    checkBorder: "var(--input-border-dark)",
    checkBorderActive: "var(--indigo-lt)",
    checkBg: "var(--indigo)",
    checkRowBg: "var(--dot-indigo)",
    gridBorder: "var(--input-border-dark)",
    accent: "var(--terra)",
    btnBg: "var(--indigo)",
    btnBgHover: "var(--indigo-lt)",
    btnText: "var(--cream)",
  },
  light: {
    text: "var(--charcoal)",
    textMuted: "var(--text-muted-on-light)",
    inputBg: "var(--input-bg)",
    inputBgFocus: "var(--input-bg-focus)",
    inputBorder: "var(--input-border)",
    inputBorderFocus: "var(--indigo)",
    checkBorder: "var(--input-border)",
    checkBorderActive: "var(--indigo)",
    checkBg: "var(--indigo)",
    checkRowBg: "hsla(246, 42%, 62%, 0.08)",
    gridBorder: "var(--input-border)",
    accent: "var(--terra)",
    btnBg: "var(--indigo)",
    btnBgHover: "var(--indigo-lt)",
    btnText: "var(--cream)",
  },
};

/* ─── Sub-components ──────────────────────────────────────────────────────── */

function Field({
  label, name, type = "text", value, onChange, error, required = false, t,
}: {
  label: string; name: string; type?: string;
  value: string; onChange: (v: string) => void;
  error?: string; required?: boolean;
  t: ThemeTokens;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label htmlFor={name} className="type-label" style={{
        display: "flex", gap: 3, alignItems: "center",
        color: focused ? t.text : t.textMuted,
        marginBottom: 8, transition: "color var(--transition-fast)",
      }}>
        {label}{required && <span style={{ color: t.accent }}>*</span>}
      </label>
      <input
        id={name} type={type} name={name} value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          display: "block", width: "100%",
          background: focused ? t.inputBgFocus : t.inputBg,
          border: `1.5px solid ${error ? t.accent : focused ? t.inputBorderFocus : t.inputBorder}`,
          color: t.text,
          fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 400,
          padding: "11px 12px",
          outline: "none", borderRadius: 0,
          transition: "border-color var(--transition-fast), background var(--transition-fast)",
        }}
      />
      {error && <p className="type-label" style={{ color: t.accent, marginTop: 4, fontSize: 10 }}>{error}</p>}
    </div>
  );
}

function TextareaField({ label, name, value, onChange, error, t, required = false }: {
  label: string; name: string; value: string; onChange: (v: string) => void;
  error?: string; t: ThemeTokens; required?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label htmlFor={name} className="type-label" style={{
        display: "flex", gap: 3, alignItems: "center",
        color: focused ? t.text : t.textMuted, marginBottom: 7,
        transition: "color var(--transition-fast)",
      }}>{label}{required && <span style={{ color: t.accent }}>*</span>}</label>
      <textarea
        id={name} name={name} value={value} rows={3}
        required={required}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          display: "block", width: "100%",
          background: focused ? t.inputBgFocus : t.inputBg,
          border: `1.5px solid ${error ? t.accent : focused ? t.inputBorderFocus : t.inputBorder}`,
          color: t.text,
          fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 400,
          padding: "11px 12px",
          outline: "none", borderRadius: 0, resize: "none",
          transition: "border-color var(--transition-fast), background var(--transition-fast)",
        }}
      />
      {error && <p className="type-label" style={{ color: t.accent, marginTop: 4, fontSize: 10 }}>{error}</p>}
    </div>
  );
}

/* ─── Main form component ─────────────────────────────────────────────────── */

export interface ContactFormProps {
  dark?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export function ContactForm({ dark = true, className = "", style }: ContactFormProps) {
  const t = dark ? themes.dark : themes.light;

  const loadTime = useRef(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    company: "", website: "", message: "",
    interests: [] as string[], honeypot: "",
  });

  const set = (k: string) => (v: string) => {
    setForm(p => ({ ...p, [k]: v }));
    // Clear field error on edit
    if (fieldErrors[k]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[k];
        return next;
      });
    }
  };

  const toggle = (label: string) => {
    setForm(p => ({
      ...p,
      interests: p.interests.includes(label)
        ? p.interests.filter(x => x !== label)
        : [...p.interests, label],
    }));
    // Clear interests error when toggling
    if (fieldErrors.interests) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next.interests;
        return next;
      });
    }
  };

  /** Client-side required-field validation. Returns true if valid. */
  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!form.firstName.trim()) errors.firstName = "First name is required.";
    if (!form.lastName.trim()) errors.lastName = "Last name is required.";
    if (!form.email.trim()) {
      errors.email = "Work email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.email = "Enter a valid email address.";
    }
    if (!form.company.trim()) errors.company = "Company is required.";
    if (form.interests.length === 0) errors.interests = "Select at least one interest.";
    if (!form.message.trim()) errors.message = "Tell us a bit about your project.";

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();

    // Anti-bot: honeypot + minimum time-to-submit
    if (form.honeypot || Date.now() - loadTime.current < 3000) return;

    // Client-side validation
    if (!validate()) return;

    // Blocked domain check (matches existing behavior)
    const domain = form.email.split("@")[1]?.toLowerCase() ?? "";
    if (BLOCKED_DOMAINS.includes(domain)) {
      setFieldErrors(prev => ({ ...prev, email: "Please use your work email." }));
      return;
    }

    setSubmitting(true);

    try {
      const { honeypot, firstName, lastName, ...rest } = form;
      // The HubSpot tracking cookie ties the server-side form submission to
      // this visitor's session — without it HubSpot spam-filters the lead.
      const hutk = document.cookie.match(/(?:^|;\s*)hubspotutk=([^;]+)/)?.[1];
      await apiRequest("POST", "/api/contact", {
        ...rest,
        name: `${firstName} ${lastName}`.trim(),
        ...getLeadAttribution(),
        ...getAdClickIds(),
        ...(hutk ? { hutk } : {}),
      });

      // GA4 conversion signal — mirrors the reminder-popup's generate_lead
      // push (GTM picks it up). This is the highest-LTV conversion on the
      // site and previously fired no client-side event at all.
      const w = window as any;
      if (Array.isArray(w.dataLayer)) {
        w.dataLayer.push({
          event: "generate_lead",
          lead_source: "contact_form",
          interests: form.interests.join(","),
        });
      }

      toast.success("Thanks — we'll be in touch within 1 business day.");
      // Reset form
      setForm({ firstName: "", lastName: "", email: "", phone: "", company: "", website: "", message: "", interests: [], honeypot: "" });
      setFieldErrors({});
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);

      // Parse status code from apiRequest error format "STATUS: body"
      const statusMatch = message.match(/^(\d{3}):\s*([\s\S]*)/);
      const status = statusMatch ? parseInt(statusMatch[1], 10) : 0;
      const body = statusMatch ? statusMatch[2] : message;

      if (status >= 400 && status < 500) {
        // Try to parse field-specific error from the API response
        try {
          const parsed = JSON.parse(body);
          if (parsed.field && parsed.message) {
            // Map server field names to form field names
            const fieldMap: Record<string, string> = {
              name: "firstName",
              email: "email",
              company: "company",
              website: "website",
              message: "message",
              interests: "interests",
            };
            const formField = fieldMap[parsed.field] || parsed.field;
            setFieldErrors(prev => ({ ...prev, [formField]: parsed.message }));
          } else {
            // Generic 4xx — show as toast, keep form values
            toast.error(parsed.message || "Something went wrong. Check your info and try again.");
          }
        } catch {
          // Body wasn't JSON or didn't have expected shape
          toast.error(body || "Something went wrong. Check your info and try again.");
        }
      } else {
        // 5xx, network error, or anything else
        toast.error("Something went wrong on our end. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className={className} style={{ display: "flex", flexDirection: "column", gap: 28, ...style }}>
      <input name="honeypot" value={form.honeypot} onChange={e => set("honeypot")(e.target.value)}
        style={{ display: "none" }} tabIndex={-1} autoComplete="off" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="First Name" name="firstName" value={form.firstName} onChange={set("firstName")} error={fieldErrors.firstName} required t={t} />
        <Field label="Last Name" name="lastName" value={form.lastName} onChange={set("lastName")} error={fieldErrors.lastName} required t={t} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Work Email" name="email" type="email" value={form.email}
          onChange={v => { set("email")(v); }}
          error={fieldErrors.email} required t={t} />
        <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={set("phone")} error={fieldErrors.phone} t={t} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Company" name="company" value={form.company} onChange={set("company")} error={fieldErrors.company} required t={t} />
        <Field label="Website" name="website" type="url" value={form.website} onChange={set("website")} error={fieldErrors.website} t={t} />
      </div>

      {/* Interests */}
      <div>
        <p className="type-label" style={{ color: t.textMuted, marginBottom: 10 }}>
          I'm interested in <span style={{ color: t.accent }}>*</span>
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2" style={{
          border: `1px solid ${fieldErrors.interests ? t.accent : t.gridBorder}`,
          transition: "border-color var(--transition-fast)",
        }}>
          {INTEREST_OPTIONS.map((interest, i) => {
            const checked = form.interests.includes(interest);
            // 2-col: vertical divider on odd items, horizontal divider on rows 2+
            const isRightCol = i % 2 === 1;
            const isSecondRowPlus = i >= 2;
            return (
              <label key={interest} onClick={() => toggle(interest)} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "9px 12px",
                background: checked ? t.checkRowBg : "transparent",
                borderTop: isSecondRowPlus ? `1px solid ${t.gridBorder}` : "none",
                borderLeft: isRightCol ? `1px solid ${t.gridBorder}` : "none",
                cursor: "pointer",
                transition: "background var(--transition-fast)",
              }}>
                <div
                  style={{
                    width: 14, height: 14, flexShrink: 0,
                    border: `1.5px solid ${checked ? t.checkBorderActive : t.checkBorder}`,
                    background: checked ? t.checkBg : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all var(--transition-fast)", cursor: "pointer",
                  }}
                >
                  {checked && (
                    <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                      <path d="M1.5 4.5l2 2L7.5 2" stroke={dark ? "var(--cream)" : "#fff"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <span className="type-body-sm" style={{ color: checked ? t.text : t.textMuted, transition: "color var(--transition-fast)", margin: 0 }}>
                  {interest}
                </span>
              </label>
            );
          })}
        </div>
        {fieldErrors.interests && <p className="type-label" style={{ color: t.accent, marginTop: 4, fontSize: 10 }}>{fieldErrors.interests}</p>}
      </div>

      <TextareaField label="Tell us more about your project" name="message" value={form.message} onChange={set("message")} error={fieldErrors.message} t={t} required />

      <button
        type="submit" disabled={submitting}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          width: "100%", padding: "16px 20px",
          background: submitting ? t.btnBgHover : t.btnBg,
          color: t.btnText, border: "none", borderRadius: 0,
          cursor: submitting ? "wait" : "pointer",
          transition: "background var(--transition-fast)",
        }}
        onMouseEnter={e => { if (!submitting) (e.currentTarget as HTMLElement).style.background = t.btnBgHover; }}
        onMouseLeave={e => { if (!submitting) (e.currentTarget as HTMLElement).style.background = t.btnBg; }}
      >
        <span className="type-label" style={{ color: t.btnText }}>
          {submitting ? "Sending..." : "Submit proposal request"}
        </span>
        {!submitting && (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3 8h10M9 4l4 4-4 4" stroke={t.btnText} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      <p className="type-body-sm" style={{ color: t.textMuted, margin: 0 }}>
        One of our experts will get back to you within the hour.
      </p>
    </form>
  );
}
